import React, { useEffect, useState } from "react";
import { BiMenu } from "react-icons/bi";
import { motion, AnimatePresence } from "framer-motion";
import { AiOutlineClose } from "react-icons/ai";

const navLinks = [
  { label: "About", href: "#about" },
  { label: "Experience", href: "#experience" },
  { label: "Projects", href: "#projects" },
];

const NavBar = () => {
  const [nav, setNav] = useState(false);
  const [active, setActive] = useState("#home");

  useEffect(() => {
    const ids = ["home", "about", "experience", "projects"];
    const onScroll = () => {
      const y = window.scrollY + 120;
      let current = "#home";
      for (const id of ids) {
        const el = document.getElementById(id);
        if (el && el.offsetTop <= y) current = `#${id}`;
      }
      setActive(current);
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <motion.header
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, ease: [0.2, 0.8, 0.2, 1] }}
        className="sticky top-4 z-40 w-full px-4 pointer-events-none"
      >
        <nav className="pointer-events-auto mx-auto max-w-3xl flex items-center justify-between gap-3 px-3 py-2 rounded-full border border-black/10 dark:border-white/10 bg-white/70 dark:bg-black/50 backdrop-blur-xl shadow-[0_8px_32px_rgba(0,0,0,0.08)]">
          <a href="#home" className="flex items-center gap-2 pl-2">
            <motion.span
              whileHover={{ rotate: 12, scale: 1.05 }}
              transition={{ type: "spring", stiffness: 300 }}
              className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-accentClr text-white text-[13px] font-bold"
            >
              SO
            </motion.span>
            <span className="hidden sm:inline text-textClr dark:text-textClrDark font-medium text-[15px]">
              Sanya<span className="text-accentClr dark:text-accentClrDark">.</span>
            </span>
          </a>

          <ul className="hidden md:flex items-center gap-1 text-sm">
            {navLinks.map((l) => {
              const isActive = active === l.href;
              return (
                <li key={l.href} className="relative">
                  <a
                    href={l.href}
                    className={`relative px-4 py-1.5 rounded-full transition-colors ${
                      isActive
                        ? "text-white dark:text-white"
                        : "text-textClr/80 dark:text-textClrDark/80 hover:text-textClr dark:hover:text-textClrDark"
                    }`}
                  >
                    {isActive && (
                      <motion.span
                        layoutId="nav-pill"
                        transition={{ type: "spring", stiffness: 380, damping: 30 }}
                        className="absolute inset-0 rounded-full bg-accentClr dark:bg-accentClrDark -z-10"
                      />
                    )}
                    <span className="relative z-10">{l.label}</span>
                  </a>
                </li>
              );
            })}
          </ul>

          <a
            href="mailto:ololadetimileyin3@gmail.com"
            className="hidden md:inline-flex items-center gap-2 text-sm font-medium rounded-full py-1.5 px-4 border border-accentClr/50 dark:border-accentClrDark/50 text-textClr dark:text-textClrDark hover:bg-accentClr hover:text-white dark:hover:bg-accentClrDark transition-colors"
          >
            Let's talk
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-accentClr dark:bg-accentClrDark" />
          </a>

          <button
            onClick={() => setNav(true)}
            className="md:hidden mr-1 text-textClr dark:text-textClrDark"
            aria-label="Open menu"
          >
            <BiMenu size={26} />
          </button>
        </nav>
      </motion.header>

      <AnimatePresence>
        {nav && (
          <motion.div
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="fixed inset-y-0 right-0 z-50 w-full md:hidden bg-white/95 dark:bg-black/95 backdrop-blur-2xl flex flex-col shadow-2xl"
          >
            {/* Mobile Nav Header */}
            <div className="flex w-full justify-between items-center py-6 px-8 border-b border-black/5 dark:border-white/5">
              <span className="text-xl font-bold tracking-tight text-textClr dark:text-textClrDark">
                Sanya<span className="text-accentClr dark:text-accentClrDark">.</span>
              </span>
              <button
                onClick={() => setNav(false)}
                className="w-10 h-10 flex items-center justify-center rounded-full border border-black/10 dark:border-white/10 text-textClr dark:text-textClrDark hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                aria-label="Close menu"
              >
                <AiOutlineClose size={20} />
              </button>
            </div>

            {/* Mobile Nav Links */}
            <div className="flex flex-col justify-center flex-1 px-8 gap-8">
              <div className="space-y-6">
                {navLinks.map((l, i) => (
                  <motion.div
                    key={l.href}
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.1 + i * 0.1, duration: 0.5 }}
                  >
                    <a
                      href={l.href}
                      onClick={() => setNav(false)}
                      className="group flex items-end gap-4"
                    >
                      <span className="text-xs font-mono opacity-30 group-hover:opacity-100 transition-opacity mb-2">
                        0{i + 1}
                      </span>
                      <span className="text-5xl font-semibold tracking-tighter text-textClr dark:text-textClrDark hover:text-accentClr dark:hover:text-accentClrDark transition-colors">
                        {l.label}
                      </span>
                    </a>
                  </motion.div>
                ))}
              </div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.5 }}
                className="pt-8"
              >
                <a
                  href="mailto:ololadetimileyin3@gmail.com"
                  onClick={() => setNav(false)}
                  className="inline-flex items-center gap-3 text-lg font-medium text-white bg-accentClr dark:bg-accentClrDark rounded-full py-4 px-10 shadow-lg shadow-accentClr/20"
                >
                  Let's talk
                  <span className="w-2 h-2 rounded-full bg-white animate-pulse" />
                </a>
              </motion.div>
            </div>

            {/* Mobile Nav Footer */}
            <div className="p-8 border-t border-black/5 dark:border-white/5 bg-black/[0.02] dark:bg-white/[0.02]">
              <p className="text-[10px] uppercase tracking-[0.2em] opacity-40 font-mono mb-6">
                Socials
              </p>
              <div className="flex gap-8">
                {["Github", "LinkedIn", "Twitter"].map((social, i) => (
                  <motion.a
                    key={social}
                    href="#"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.5 + i * 0.1 }}
                    className="text-sm font-medium opacity-60 hover:opacity-100 hover:text-accentClr dark:hover:text-accentClrDark transition-all"
                  >
                    {social}
                  </motion.a>
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default NavBar;
